import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router';
import {
  ANTHROPOMETRY,
  PHONE_TILT,
  STANDING_ALIGNMENT,
  THRESHOLDS_VERSION,
  gradeStanding,
  sideLandmarks,
} from '@erectus/shared';
import type { FrameRecord, GateReason, Landmark, PostureCheck, Side } from '@erectus/shared';
import { Button } from '@/ui/primitives.js';
import { BottomSheet, COLLAPSED_SHEET_CSS } from '@/ui/BottomSheet.js';
import { Icon } from '@/ui/icons.js';
import { SETUP_CHECKS, checkPassed } from '@/pose/qualityGate.js';
import { standingChecks } from '@/pose/standingGate.js';
import { medianAlignment, standingAlignment, type StandingAlignment } from '@/metrics/standing.js';
import { appConfig } from '@/lib/remoteConfig.js';
import { deviceClass, effectiveFps, effectivePoseModel } from '@/lib/device.js';
import { readEnv } from '@/lib/env.js';
import { newPostureCheckId, saveCalibration, saveHeight, savePostureCheck } from '@/lib/repo.js';
import { track } from '@/lib/analytics.js';
import { playCue } from '@/lib/cues.js';
import { currentImageRoll, currentPitch, medianTilt } from '@/lib/motion.js';
import { levelLandmarks } from '@/pose/level.js';
import { quantize } from '@/session/db.js';
import { uploadCheckFrames } from '@/session/upload.js';
import { useAuth } from '@/stores/auth.js';
import { newSessionId, rememberFacing, useSession } from '@/stores/session.js';
import { useCapture } from '@/features/capture/useCapture.js';
import { useAutoStart } from '@/features/capture/useAutoStart.js';
import { phoneRow, useTilt } from '@/features/capture/useTilt.js';
import { SkeletonOverlay } from '@/features/capture/SkeletonOverlay.js';
import { CameraFlipButton } from '@/features/capture/CameraFlipButton.js';
import { GateChecklist } from '@/features/capture/GateChecklist.js';
import { ArmedIndicator } from '@/features/capture/ArmedIndicator.js';
import { CameraLoading } from '@/features/capture/CameraLoading.js';
import { CheckResult } from './CheckResult.js';
import { HeightField } from './HeightField.js';

/**
 * Posture check — two separate short measurements beside the desk session:
 *
 *   Sitting baseline     sit tall, 5 s  → the baseline desk sessions compare with
 *   Standing alignment   stand side-on, head to feet, 10 s → alignment grades
 *
 * Each starts the way a desk session does: "Start when I'm in place" (the
 * phone is across the room; checks held 3 s, then a chime) or "Start now"
 * (someone at the phone, everything already passing). Nothing sounds when
 * recognition fails — the checklist says what is wrong.
 *
 * What is kept: medians and the spread (SD) within the check, the conditions
 * (phone tilt, how much of the frame the body fills, camera, model) and, with
 * the detection-improvement opt-in only, the raw frames.
 */
type Kind = PostureCheck['kind'];
type Stage = 'ready' | 'wait' | 'measure' | 'saving' | 'done' | 'failed';

const KIND_KEY = 'erectus.checkKind';
const MEASURE_MS: Record<Kind, number> = { seated: 5000, standing: 10_000 };
const SHEET_SNAPS: [number, number, number] = [0.09, 0.31, 0.78];

const COPY: Record<Kind, { tab: string; title: string; detail: string; steps: string }> = {
  seated: {
    tab: 'Sitting baseline',
    title: 'Sit tall',
    detail: 'Side-on, as you sit when you sit well.',
    steps:
      'Five seconds sitting tall, side-on. Desk sessions then compare you with this — your own baseline, not just the published line.',
  },
  standing: {
    tab: 'Standing alignment',
    title: 'Stand side-on',
    detail: 'Feet hip-width, weight even, looking ahead. Head to feet in frame.',
    steps: 'Ten seconds standing side-on, head to feet in frame. Graded against measured healthy adults.',
  },
};

function storedKind(): Kind {
  try {
    return window.localStorage.getItem(KIND_KEY) === 'standing' ? 'standing' : 'seated';
  } catch {
    return 'seated';
  }
}

export function PostureCheckPage(): ReactNode {
  const navigate = useNavigate();
  const { user, userDoc, refreshUserDoc } = useAuth();
  const session = useSession();
  const config = appConfig();
  const cls = deviceClass();
  const poseModel = effectivePoseModel(config.poseModel, cls);
  const sampleFps = effectiveFps(config.sampleFps, cls);
  const sessionIdRef = useRef(newSessionId());

  const capture = useCapture({
    sessionId: sessionIdRef.current,
    ...(user ? { uid: user.uid } : {}),
    sampleFps,
    poseModel,
    calibration: null,
    facing: session.facing,
    onFacingChange: (facing) => {
      session.configure({ facing });
      rememberFacing(facing);
    },
  });
  const { tilt, enable: enableTilt } = useTilt();

  const [kind, setKind] = useState<Kind>(storedKind);
  const [stage, setStage] = useState<Stage>('ready');
  const [sheetSnap, setSheetSnap] = useState<0 | 1 | 2>(1);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<PostureCheck | null>(null);

  const buffer = useRef<{
    startedAt: number;
    frames: FrameRecord[];
    seated: Array<{
      cva: number | null;
      trunk: number | null;
      prot: number | null;
    }>;
    standing: StandingAlignment[];
    rolls: number[];
    pitches: number[];
    standingRaw: StandingAlignment[];
    span: number[];
  }>({
    startedAt: 0,
    frames: [],
    seated: [],
    standing: [],
    rolls: [],
    pitches: [],
    standingRaw: [],
    span: [],
  });

  const tick = capture.tick;
  const video = capture.videoRef.current;
  const aspect = video && video.videoHeight > 0 ? video.videoWidth / video.videoHeight : 0.75;

  const checks = useMemo(() => {
    if (kind === 'standing') return standingChecks(tick?.gate, tick?.landmarks ?? [], aspect, tilt);
    const reasons = tick?.gate.reasons ?? (['noPose'] as GateReason[]);
    const rows: Array<{ id: string; label: string; passed: boolean }> = SETUP_CHECKS.map((c) => ({
      id: c.id,
      label: c.label,
      passed: checkPassed(c, reasons),
    }));
    if (tilt !== null)
      rows.splice(1, 0, phoneRow(tilt));
    return rows;
  }, [aspect, kind, tick, tilt]);
  const allPassed = capture.modelReady && checks.every((c) => c.passed);

  const auto = useAutoStart({
    conditionsMet: allPassed && stage === 'wait',
    onStart: () => measure(),
  });

  function measure(): void {
    buffer.current = {
      startedAt: performance.now(),
      frames: [],
      seated: [],
      standing: [],
      rolls: [],
    pitches: [],
    standingRaw: [],
      span: [],
    };
    setStage('measure');
    setSheetSnap(0);
    window.setTimeout(() => void finish(), MEASURE_MS[kind]);
  }

  // One sample per measurable tick while measuring.
  useEffect(() => {
    if (stage !== 'measure' || !tick || !allPassed) return;
    const b = buffer.current;
    b.frames.push({
      t: Math.round(performance.now() - b.startedAt),
      state: tick.state,
      cva: tick.metrics.cva,
      cha: tick.metrics.cha,
      trunk: tick.metrics.trunkInclination,
      prot: tick.metrics.shoulderProtraction,
      hip: tick.metrics.hipAngle,
      knee: tick.metrics.kneeAngle,
      lm: quantize(tick.landmarks),
    });
    const roll = currentImageRoll(capture.facing);
    const pitch = currentPitch();
    if (roll !== null) b.rolls.push(roll);
    if (pitch !== null) b.pitches.push(pitch);
    const span = bodySpan(tick.landmarks, tick.gate.side, kind);
    if (span !== null) b.span.push(span);
    if (kind === 'seated') {
      b.seated.push({
        cva: tick.metrics.cva,
        trunk: tick.metrics.trunkInclination,
        prot: tick.metrics.shoulderProtraction,
      });
    } else {
      // Same levelling as the recorder applies to the seated metrics.
      const level = roll !== null && Math.abs(roll) <= PHONE_TILT.maxRollDeg ? roll : 0;
      const levelled = level === 0 ? tick.landmarks : levelLandmarks(tick.landmarks, aspect, level);
      const a = standingAlignment(levelled, tick.gate.side, aspect);
      const raw = standingAlignment(tick.landmarks, tick.gate.side, aspect);
      if (raw) b.standingRaw.push(raw);
      if (a) b.standing.push(a);
    }
  }, [allPassed, aspect, capture.facing, kind, stage, tick]);

  async function finish(): Promise<void> {
    const b = buffer.current;
    const expected = (MEASURE_MS[kind] / 1000) * sampleFps;
    const count = kind === 'seated' ? b.seated.length : b.standing.length;
    if (count < Math.ceil(expected / 2)) {
      fail(
        kind === 'seated'
          ? 'Could not see you sitting clearly for long enough. Check the list on screen and try again.'
          : 'Could not see you standing clearly for long enough. Check the list on screen and try again.',
      );
      return;
    }
    playCue('finish');
    setStage('saving');

    const heightCm = userDoc?.profile?.heightCm ?? null;
    let seated: PostureCheck['seated'] = null;
    let standing: PostureCheck['standing'] = null;
    if (kind === 'seated') {
      const cva = b.seated.map((s) => s.cva);
      const trunk = b.seated.map((s) => s.trunk);
      const prot = b.seated.map((s) => s.prot);
      seated = {
        cva: med(cva),
        trunk: med(trunk),
        prot: med(prot, 3),
        cvaSd: sd(cva),
        trunkSd: sd(trunk),
        protSd: sd(prot, 3),
      };
    } else {
      const m = medianAlignment(b.standing)!;
      const rawMedian = medianAlignment(b.standingRaw);
      const earToAnkleCm = heightCm ? heightCm * ANTHROPOMETRY.earToAnkleFraction : null;
      standing = {
        earOffsetPct: round(m.earOffsetPct),
        shoulderOffsetPct: round(m.shoulderOffsetPct),
        hipOffsetPct: round(m.hipOffsetPct),
        kneeOffsetPct: round(m.kneeOffsetPct),
        bodyLineDeg: round(m.bodyLineDeg),
        shoulderLineDeg: round(m.shoulderLineDeg),
        bodyLineDegRaw: round(rawMedian?.bodyLineDeg ?? m.bodyLineDeg),
        shoulderLineDegRaw: round(rawMedian?.shoulderLineDeg ?? m.shoulderLineDeg),
        bodyLineSd: sd(b.standing.map((s) => s.bodyLineDeg)) ?? 0,
        shoulderLineSd: sd(b.standing.map((s) => s.shoulderLineDeg)) ?? 0,
        cva: m.cva === null ? null : round(m.cva),
        earOffsetCm: earToAnkleCm === null ? null : round((m.earOffsetPct / 100) * earToAnkleCm),
        shoulderOffsetCm: earToAnkleCm === null ? null : round((m.shoulderOffsetPct / 100) * earToAnkleCm),
        grades: gradeStanding(m),
        version: STANDING_ALIGNMENT.version,
      };
    }

    const check: PostureCheck = {
      kind,
      at: Date.now(),
      durationMs: MEASURE_MS[kind],
      sampleCount: count,
      sampleFps,
      seated,
      standing,
      phoneRollDeg: medianTilt(b.rolls),
      phonePitchDeg: medianTilt(b.pitches),
      bodyFrameFraction: med(b.span, 3),
      heightCm: heightCm ? Math.round(heightCm) : null,
      keypointPath: null,
      poseModel,
      facing: capture.facing,
      deviceClass: cls,
      appVersion: readEnv().appVersion,
      thresholdsVersion: THRESHOLDS_VERSION,
    };

    if (user) {
      const id = newPostureCheckId(user.uid);
      check.keypointPath = await uploadCheckFrames({
        uid: user.uid,
        checkId: id,
        frames: b.frames,
        sampleFps,
        dataOptIn: userDoc?.consent?.dataOptIn ?? false,
      });
      try {
        await savePostureCheck(user.uid, id, check);
        if (seated && seated.cva != null) {
          await saveCalibration(user.uid, {
            cvaBaseline: seated.cva,
            trunkBaseline: seated.trunk,
            protBaseline: seated.prot,
            measuredAt: check.at,
            sampleCount: count,
          });
        }
        await refreshUserDoc();
      } catch {
        setError('Shown here only — it could not be saved. Check your connection.');
      }
    }
    track('posture_check_done', { kind });
    setResult(check);
    setStage('done');
  }

  function fail(message: string): void {
    auto.disarm();
    setError(message);
    setStage('failed');
    setSheetSnap(1);
  }

  const chooseKind = (next: Kind): void => {
    if (stage === 'wait' || stage === 'measure') return;
    setKind(next);
    setStage('ready');
    setError(null);
    try {
      window.localStorage.setItem(KIND_KEY, next);
    } catch {
      // Remembering the choice is a convenience.
    }
  };

  const startHandsFree = (): void => {
    enableTilt();
    setError(null);
    setSheetSnap(0);
    setStage('wait');
    void auto.arm();
  };

  const startNow = (): void => {
    enableTilt();
    setError(null);
    measure();
  };

  const cancel = (): void => {
    auto.disarm();
    setStage('ready');
    setSheetSnap(1);
  };

  if (stage === 'done' && result) {
    return (
      <CheckResult
        check={result}
        note={error}
        onAgain={() => {
          setResult(null);
          setError(null);
          setStage('ready');
          setSheetSnap(1);
        }}
      />
    );
  }

  const copy = COPY[kind];
  const busy = stage === 'wait' || stage === 'measure';
  const checklistBottom =
    sheetSnap === 0
      ? `calc(${COLLAPSED_SHEET_CSS} + 0.5rem)`
      : `calc(${SHEET_SNAPS[sheetSnap] * 100}dvh + env(safe-area-inset-bottom) + 0.75rem)`;

  return (
    <div className="fixed inset-0 overflow-hidden bg-ink">
      <div
        className="absolute inset-0"
        style={{
          transform: capture.facing === 'user' ? 'scaleX(-1)' : undefined,
        }}
      >
        <video
          ref={capture.videoRef}
          playsInline
          muted
          className={`absolute inset-0 size-full object-cover transition-opacity duration-200 ${capture.previewReady ? 'opacity-100' : 'opacity-0'}`}
          aria-label="Camera preview"
        />
        <SkeletonOverlay
          landmarks={tick?.landmarks ?? []}
          videoRef={capture.videoRef}
          side={tick?.gate.side}
          fit="cover"
        />
      </div>

      <div className="absolute inset-x-0 top-0 z-10 flex items-start justify-between gap-2 bg-gradient-to-b from-ink/70 to-transparent px-4 pt-[calc(0.75rem+env(safe-area-inset-top))] pb-8 text-white">
        <div className="flex items-start gap-2">
          <button
            onClick={() => navigate('/progress')}
            aria-label="Close posture check"
            className="grid size-8 shrink-0 place-items-center rounded-full bg-white/15 backdrop-blur"
          >
            <Icon name="close" size={16} strokeWidth={2.2} />
          </button>
          <div>
            <p className="text-sm font-semibold">{copy.tab}</p>
            <p className="text-xs opacity-75">
              {kind === 'seated' ? 'Side-on · head to knees in frame' : 'Side-on · head to feet in frame'}
            </p>
          </div>
        </div>
        <CameraFlipButton
          facing={capture.facing}
          onFlip={() => void capture.flipCamera()}
          disabled={!capture.streamReady || busy}
        />
      </div>

      <div className="pointer-events-none absolute inset-x-0 top-24 z-10 flex flex-col items-center gap-3 px-4 text-center text-white">
        {busy ? (
          <div style={{ textShadow: '0 1px 3px rgba(15,23,42,0.9)' }}>
            <p className="text-3xl font-bold">{stage === 'measure' ? 'Hold still' : copy.title}</p>
            <p className="text-sm opacity-90">
              {stage === 'measure' ? `Measuring ${MEASURE_MS[kind] / 1000} seconds…` : copy.detail}
            </p>
          </div>
        ) : null}
        {auto.armed ? (
          <ArmedIndicator
            progress={auto.progress}
            secondsLeft={auto.secondsLeft}
            allPassed={allPassed}
            failing={checks.filter((c) => !c.passed).map((c) => c.label)}
            holdLabel="Hold still — measuring next"
          />
        ) : null}
      </div>

      {capture.error ? (
        <div className="absolute inset-x-4 top-40 z-20 rounded-2xl bg-white p-4 text-sm shadow-lg">
          <p className="font-semibold">Camera problem</p>
          <p className="mt-1 text-ink-soft">{capture.error.message}</p>
          <Button variant="secondary" className="mt-3" onClick={() => window.location.reload()}>
            Try again
          </Button>
        </div>
      ) : null}

      {stage === 'saving' ? null : (
        <div className="pointer-events-none absolute inset-x-0 z-10 px-3" style={{ bottom: checklistBottom }}>
          <GateChecklist
            checks={checks}
            reasons={kind === 'standing' ? [] : (tick?.gate.reasons ?? [])}
            loading={!capture.modelReady}
          />
        </div>
      )}

      <BottomSheet
        snaps={SHEET_SNAPS}
        snap={sheetSnap}
        onSnapChange={setSheetSnap}
        header={
          <span className="flex items-center justify-between pt-1">
            <span className="text-sm font-semibold">{copy.tab}</span>
            <span className="text-xs text-muted">{MEASURE_MS[kind] / 1000} seconds</span>
          </span>
        }
      >
        <div className="space-y-3 pt-2">
          <div
            className="grid grid-cols-2 rounded-full bg-surface p-0.5 text-xs font-semibold"
            role="tablist"
            aria-label="Session type"
          >
            <Link
              role="tab"
              aria-selected={false}
              to="/capture"
              replace
              className="rounded-full py-1.5 text-center text-ink-soft"
            >
              Desk session
            </Link>
            <span role="tab" aria-selected className="rounded-full bg-ink py-1.5 text-center text-white">
              Posture check
            </span>
          </div>
          <div className="grid grid-cols-2 gap-1.5" role="radiogroup" aria-label="Which check">
            {(['seated', 'standing'] as const).map((k) => (
              <button
                key={k}
                type="button"
                role="radio"
                aria-checked={kind === k}
                disabled={busy}
                onClick={() => chooseKind(k)}
                className={`rounded-xl border px-2 py-2 text-sm font-semibold ${kind === k ? 'border-ink bg-ink text-white' : 'border-line bg-white text-ink-soft'}`}
              >
                {COPY[k].tab}
              </button>
            ))}
          </div>

          {error ? <p className="text-sm text-[color:var(--color-state-slouch)]">{error}</p> : null}

          {busy ? (
            <Button variant="secondary" className="w-full" onClick={cancel}>
              Cancel
            </Button>
          ) : stage === 'saving' ? (
            <p className="text-sm text-muted">Saving…</p>
          ) : (
            <div className="grid gap-2">
              <Button className="w-full" disabled={!capture.modelReady} onClick={startHandsFree}>
                Start when I&apos;m in place
              </Button>
              <Button variant="secondary" className="w-full" disabled={!allPassed} onClick={startNow}>
                Start now
              </Button>
            </div>
          )}
          <p className="text-xs text-muted">{copy.steps}</p>

          {kind === 'standing' && user ? (
            <div className="border-t border-line pt-3">
              <HeightField
                heightCm={userDoc?.profile?.heightCm ?? null}
                onSave={(cm) =>
                  void saveHeight(user.uid, cm)
                    .then(refreshUserDoc)
                    .catch(() => undefined)
                }
              />
            </div>
          ) : null}
        </div>
      </BottomSheet>
      <CameraLoading ready={capture.previewReady || Boolean(capture.error)} onClose={() => navigate('/progress')} />
    </div>
  );
}

/** Ear→hip (seated) or ear→ankle (standing) as a share of the frame height. */
function bodySpan(landmarks: readonly Landmark[], side: Side, kind: Kind): number | null {
  const idx = sideLandmarks(side);
  const ear = landmarks[idx.ear];
  const low = landmarks[kind === 'seated' ? idx.hip : idx.ankle];
  if (!ear || !low) return null;
  const span = low.y - ear.y;
  return span > 0 ? span : null;
}

function med(values: ReadonlyArray<number | null>, digits = 1): number | null {
  const v = values.filter((x): x is number => x !== null && Number.isFinite(x)).sort((a, b) => a - b);
  if (v.length === 0) return null;
  return round(v[Math.floor(v.length / 2)]!, digits);
}

function sd(values: ReadonlyArray<number | null>, digits = 2): number | null {
  const v = values.filter((x): x is number => x !== null && Number.isFinite(x));
  if (v.length < 2) return null;
  const mean = v.reduce((s, x) => s + x, 0) / v.length;
  const variance = v.reduce((s, x) => s + (x - mean) ** 2, 0) / (v.length - 1);
  return round(Math.sqrt(variance), digits);
}

function round(value: number, digits = 1): number {
  const f = 10 ** digits;
  return Math.round(value * f) / f;
}
